import React, { useState } from "react";
import { Eye, Inbox, X, Truck, ArrowRight, FileText } from "lucide-react";

interface ClinicLabtestListingTableProps {
  orders: any[];
  loading: boolean;
  activeTab: string;
  tabLabel: string;
  onAction: (order: any) => void;
  currentPage?: number;
  pageSize?: number;
}

const ClinicLabtestListingTable: React.FC<ClinicLabtestListingTableProps> = ({
  orders,
  loading,
  activeTab,
  tabLabel,
  onAction,
  currentPage = 1,
  pageSize = 10,
}) => {
  const [pdfBillUrl, setPdfBillUrl] = useState<string | null>(null);

  const getActionForOrder = (order: any) => {
    const status = order.orderStatus?.toLowerCase() || "";
    if (status === "clinic_assigned") return "assign_rider";
    if (status === "pickup_completed") return "forward_to_lab";
    return "view";
  };

  const getActionLabel = (order: any) => {
    const action = getActionForOrder(order);
    if (action === "assign_rider") return "Assign Rider";
    if (action === "forward_to_lab") return "Forward to Lab";
    return "View";
  };

  const actionStyles: Record<string, string> = {
    assign_rider:
      "bg-[#fef9e7] border border-[#f59e0b] text-[#b45309] hover:bg-[#fde68a] hover:text-[#92400e] transition-colors shadow-sm",
    forward_to_lab:
      "bg-[#e8f0fe] border border-[#1a73e8] text-[#1a73e8] hover:bg-[#d2e3fc] hover:text-[#1557b0] transition-colors shadow-sm",
    view: "bg-slate-100 border border-slate-200 text-slate-600 hover:bg-slate-200 transition-colors shadow-sm",
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusLabel = (status: string) => {
    if (!status) return "—";
    return status.replace(/_/g, " ").toUpperCase();
  };

  const getStatusStyle = (status: string) => {
    if (!status) return "text-slate-500 bg-slate-50 border border-slate-200";
    const clean = status.toLowerCase();
    if (clean === "clinic_assigned") {
      return "text-amber-600 bg-amber-50 border border-amber-200";
    }
    if (clean === "pickup_assigned") {
      return "text-indigo-600 bg-indigo-50 border border-indigo-200";
    }
    if (clean === "out_for_pickup") {
      return "text-purple-600 bg-purple-50 border border-purple-200";
    }
    if (clean === "pickup_completed") {
      return "text-cyan-600 bg-cyan-50 border border-cyan-200";
    }
    if (clean === "moved_to_lab") {
      return "text-blue-600 bg-blue-50 border border-blue-200";
    }
    if (clean === "report_generated") {
      return "text-emerald-600 bg-emerald-50 border border-emerald-200";
    }
    if (clean === "cancelled") {
      return "text-rose-600 bg-rose-50 border border-rose-200";
    }
    return "text-indigo-600 bg-indigo-50 border border-indigo-200";
  };

  const headers =
    activeTab === "completed"
      ? ["S.No.", "Order ID", "Date", "Patient", "Tests", "Amount", "Status", "Action", "Bill"]
      : ["S.No.", "Order ID", "Date", "Patient", "Tests", "Amount", "Status", "Action"];

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-y-auto hiddenScrollbar relative">
      <div className="overflow-x-auto">
        <table className="w-full text-[0.8rem] min-w-[900px]">
          <thead>
            <tr className="bg-slate-50 sticky top-0 z-[2]">
              {headers.map((h) => (
                <th
                  key={h}
                  className={`px-3 py-2.5 text-left font-semibold text-slate-500 border-b border-slate-200 whitespace-nowrap uppercase text-[0.65rem] tracking-wider ${
                    h === "Status" ? "min-w-[140px] w-[140px]" : ""
                  }`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {headers.map((_, j) => (
                    <td key={j} className="px-3 py-3">
                      <div
                        className="h-3.5 rounded bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 animate-pulse"
                        style={{ width: j === 3 ? 120 : 60 }}
                      />
                    </td>
                  ))}
                </tr>
              ))
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan={headers.length}>
                  <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                    <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                      <Inbox size={24} />
                    </div>
                    <p className="text-sm font-semibold text-slate-500">
                      No {tabLabel.toLowerCase()} labtest orders found
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              orders.map((order, orderIndex) => {
                const action = getActionForOrder(order);
                const statusStyle = getStatusStyle(order.orderStatus);
                const statusLabel = getStatusLabel(order.orderStatus);

                return (
                  <tr
                    key={order.orderId || order.id}
                    className="hover:bg-slate-50/80 transition-colors border-b border-slate-50 last:border-b-0"
                  >
                    {/* S.No. */}
                    <td className="px-3 py-2.5">
                      <span className="text-xs font-medium text-slate-500">
                        {(currentPage - 1) * pageSize + orderIndex + 1}
                      </span>
                    </td>
                    {/* Order ID */}
                    <td className="px-3 py-2.5">
                      <span className="font-semibold text-emerald-600 text-xs">
                        #{order.orderNumber}
                      </span>
                    </td>
                    {/* Date */}
                    <td className="px-3 py-2.5">
                      <div className="font-semibold text-slate-700 text-xs">
                        {formatDate(order.orderDate)}
                      </div>
                    </td>
                    {/* Patient */}
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        {(order.patientImageUrl || order.patientDetails?.patientProfileImage) && (
                          <img
                            src={order.patientImageUrl || order.patientDetails?.patientProfileImage}
                            alt=""
                            className="w-6 h-6 rounded-full object-cover border border-slate-200"
                          />
                        )}
                        <div>
                          <div className="font-semibold text-slate-800 text-xs">
                            {order.patientName || order.patientDetails?.patientName || "—"}
                          </div>
                          <div className="text-[0.65rem] text-slate-400">
                            {order.patientPhone || order.patientPhoneNumber || order.patientDetails?.patientMobileNumber || ""}
                          </div>
                        </div>
                      </div>
                    </td>
                    {/* Tests */}
                    <td className="px-3 py-2.5">
                      <span className="bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full font-semibold text-[0.7rem]">
                        {order.testsCount || (Array.isArray(order.labtestItemsDetails) ? order.labtestItemsDetails.length : 1)} Test
                        {(order.testsCount || (Array.isArray(order.labtestItemsDetails) ? order.labtestItemsDetails.length : 1)) !== 1 ? "s" : ""}
                      </span>
                    </td>
                    {/* Amount */}
                    <td className="px-3 py-2.5">
                      <span className="font-bold text-slate-800 text-xs">₹{order.totalAmount || order.orderTotal}</span>
                    </td>
                    {/* Status */}
                    <td className="px-3 py-2.5 min-w-[140px] w-[140px]">
                      <span
                        className={`px-2 py-1 rounded-lg font-bold text-[0.62rem] tracking-wide inline-block ${statusStyle}`}
                      >
                        {statusLabel}
                      </span>
                    </td>
                    {/* Action */}
                    <td className="px-3 py-2.5">
                      <button
                        onClick={() => onAction(order)}
                        className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[0.7rem] font-bold cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md ${
                          actionStyles[action] || ""
                        }`}
                      >
                        {action === "assign_rider" && <Truck size={12} />}
                        {action === "forward_to_lab" && <ArrowRight size={12} />}
                        {action === "view" && <Eye size={12} />}
                        {getActionLabel(order)}
                      </button>
                    </td>
                    {/* Bill (completed tab only) */}
                    {activeTab === "completed" && (
                      <td className="px-3 py-2.5">
                        {(order.bill || order.billUrl) ? (
                          <button
                            onClick={() => setPdfBillUrl(order.bill || order.billUrl || null)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[0.7rem] font-bold bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-500 hover:text-white transition-all cursor-pointer shadow-sm"
                          >
                            <FileText size={12} />
                            Bill
                          </button>
                        ) : (
                          <span className="text-slate-350 text-xs font-semibold">Not Generated</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Bill Preview Modal */}
      {pdfBillUrl && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200000] flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => setPdfBillUrl(null)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-4xl h-[85vh] flex flex-col shadow-2xl overflow-hidden animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white">
              <h3 className="text-sm font-bold text-slate-800">Order Invoice / Bill</h3>
              <button
                className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors border-none cursor-pointer"
                onClick={() => setPdfBillUrl(null)}
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 bg-slate-100">
              <iframe
                src={`${pdfBillUrl}#toolbar=0`}
                className="w-full h-full border-none"
                title="Invoice Bill PDF"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClinicLabtestListingTable;